/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useQuery, useApolloClient, gql } from '@apollo/client';
import { PDFViewer } from '@react-pdf/renderer';
import { Content } from '../../components/light/Content';
import { SectionBlock } from '../../components/light/SectionBlock';
import { Loading } from '../../components/Loading';
import { SellerContractPdfDocument } from '../../components/pdf/SellerContractPdfDocument';

const QUERY_SUPPLIER_CONTRACT_LIST = gql`
  query supplierContractList($year: Int, $supid: Int, $name: String, $limit: Int, $offset: Int) {
    supplierContractList(year: $year, supid: $supid, name: $name, limit: $limit, offset: $offset) {
      items {
        supid
        year
        name
        contact
        legalRep
      }
      total
    }
  }
`;

const QUERY_SUPPLIER_CONTRACT_DETAIL = gql`
  query supplierContractDetail($supid: Int!, $year: Int!) {
    supplierContractDetail(supid: $supid, year: $year) {
      supid
      year
      month
      legalRep
      commission
      handlingFee
      pickupFee
      storageFee
      processingFee
      removalFee
    }
  }
`;

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

export function SellerContractPreviewScreen(): React.ReactElement {
  const queryParams = useQueryParams();
  const history = useHistory();
  const client = useApolloClient();

  const initialSupId = Number(queryParams.get('supid')) || 0;
  const initialYear = Number(queryParams.get('year')) || 2021;

  const [supidInput, setSupidInput] = useState<string>(initialSupId ? String(initialSupId) : '');
  const [selectedYear, setSelectedYear] = useState<number>(initialYear);
  const [activeContract, setActiveContract] = useState<any | null>(null);
  const [activeDetails, setActiveDetails] = useState<any[]>([]);
  const [isLoadingContract, setIsLoadingContract] = useState<boolean>(false);

  // Load contract list if supid is not initially passed
  const { data: listData } = useQuery(QUERY_SUPPLIER_CONTRACT_LIST, {
    variables: { year: selectedYear, limit: 10, offset: 0 },
    fetchPolicy: 'network-only',
  });

  const loadContractData = async (targetSupId: number, targetYear: number) => {
    try {
      setIsLoadingContract(true);

      // 1. Fetch list item for seller info
      const listRes = await client.query({
        query: QUERY_SUPPLIER_CONTRACT_LIST,
        variables: { year: targetYear, supid: targetSupId, limit: 1 },
        fetchPolicy: 'network-only',
      });

      const items = listRes.data?.supplierContractList?.items || [];
      const item = items[0] || {
        supid: targetSupId,
        year: targetYear,
        name: `Seller ${targetSupId}`,
        contact: { email1: '', contact1: '' },
        legalRep: '',
      };

      // 2. Fetch monthly contract details
      const detailRes = await client.query({
        query: QUERY_SUPPLIER_CONTRACT_DETAIL,
        variables: { supid: targetSupId, year: targetYear },
        fetchPolicy: 'network-only',
      });

      const details = detailRes.data?.supplierContractDetail || [];

      setActiveContract(item);
      setActiveDetails(details);
    } catch (err) {
      console.error('Failed to load seller contract for preview:', err);
    } finally {
      setIsLoadingContract(false);
    }
  };

  useEffect(() => {
    if (initialSupId > 0) {
      loadContractData(initialSupId, initialYear);
    } else if (listData?.supplierContractList?.items?.length > 0) {
      const firstItem = listData.supplierContractList.items[0];
      setSupidInput(String(firstItem.supid));
      loadContractData(firstItem.supid, selectedYear);
    } else {
      // Fallback demo values if backend has no data yet
      setActiveContract({
        supid: 1001,
        year: selectedYear,
        name: 'Sample Store',
        contact: { email1: 'sample@l192.com', contact1: '012345678' },
        legalRep: 'Sample Representative',
      });
      setActiveDetails([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSupId, initialYear, listData]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const id = Number(supidInput.trim());
    if (id > 0) {
      history.push(`/audit/seller-contract-preview?supid=${id}&year=${selectedYear}`);
      loadContractData(id, selectedYear);
    }
  };

  const years = [2021, 2022, 2023, 2024, 2025, 2026, 2027];

  return (
    <Content>
      <SectionBlock title="Seller Contract PDF Live Preview">
        <div
          className="controls-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 15,
            padding: '10px 15px',
            backgroundColor: '#f8f9fa',
            borderRadius: 6,
            border: '1px solid #e9ecef',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <form
            onSubmit={handleSearchSubmit}
            style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <div>
              <label style={{ marginRight: 6, fontWeight: 600 }}>Year:</label>
              <select
                className="form-control"
                style={{ width: 110, display: 'inline-block' }}
                value={selectedYear}
                onChange={e => {
                  const y = Number(e.target.value);
                  setSelectedYear(y);
                  if (activeContract?.supid) {
                    history.push(`/audit/seller-contract-preview?supid=${activeContract.supid}&year=${y}`);
                    loadContractData(activeContract.supid, y);
                  }
                }}
              >
                {years.map(y => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ marginRight: 6, fontWeight: 600 }}>Seller ID:</label>
              <input
                type="number"
                className="form-control"
                placeholder="Enter Seller ID..."
                style={{ width: 160, display: 'inline-block' }}
                value={supidInput}
                onChange={e => setSupidInput(e.target.value)}
              />
            </div>

            <button type="submit" className="btn btn-primary">
              <i className="fas fa-search" style={{ marginRight: 5 }}></i> Load Preview
            </button>
          </form>

          <button className="btn btn-outline-secondary btn-sm" onClick={() => history.push('/audit/seller-contract')}>
            <i className="fas fa-arrow-left" style={{ marginRight: 5 }}></i> Back to Seller List
          </button>
        </div>

        {isLoadingContract ? (
          <Loading />
        ) : activeContract ? (
          <div style={{ width: '100%', height: '82vh' }}>
            <PDFViewer style={{ width: '100%', height: '100%', border: '1px solid #ced4da', borderRadius: 6 }}>
              <SellerContractPdfDocument contract={activeContract} details={activeDetails} />
            </PDFViewer>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
            Please select a Seller ID and Year to preview the contract.
          </div>
        )}
      </SectionBlock>
    </Content>
  );
}
